import React, { useState, useEffect, memo } from 'react';
import {
  FormControl,
  FormLabel,
  Input,
  Select,
  Button,
  VStack,
  Box,
  Text,
  Divider,
  Checkbox,
  Textarea,
  HStack,
  IconButton,
  Progress,
  Alert,
  AlertIcon,
  AlertDescription,
} from '@chakra-ui/react';
import { DeleteIcon } from '@chakra-ui/icons';
import { getSupabase, supabasePublic } from './supabaseClient';
import { BASE_URLS } from './env';

/**
 * SendFaxTabForm - A standalone form component for the Send tab
 * 
 * This component manages its own local state to prevent parent re-renders during typing.
 */
const SendFaxTabForm = memo(({
  // File management props
  selectedFax,
  uploadedFile,
  uploadingFile,
  uploadProgress,
  uploadError,
  onFileUpload,
  onRemoveUploadedFile,
  allFaxes = [],
  onSelectFax,
  // Contacts
  faxContacts = [],
  loadingContacts,
  onAddContact,
  // Email options
  srfaxAuthEmails = [],
  // Schema for isolation
  userSchema = 'public',
  // Callbacks
  toast,
  onSendSuccess,
}) => {
  // Local form state - completely isolated from parent
  const [formData, setFormData] = useState({
    fax_number: '1',
    recipient_name: '',
    sender_name: '',
    sender_fax: '',
    sender_email: srfaxAuthEmails.length > 0 ? srfaxAuthEmails[0] : '',
    subject: '',
    cover_page: false,
    cover_page_text: '',
  });
  
  const [sendingFax, setSendingFax] = useState(false);

  // Update default email when srfaxAuthEmails changes
  useEffect(() => {
    if (srfaxAuthEmails.length > 0 && !formData.sender_email) {
      setFormData(prev => ({ ...prev, sender_email: srfaxAuthEmails[0] }));
    }
  }, [srfaxAuthEmails]);

  // Simple field update - no debouncing needed since state is local
  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Select a contact and populate fields
  const selectContact = (contact) => {
    setFormData(prev => ({
      ...prev,
      fax_number: contact.fax_number || '1',
      recipient_name: contact.name || '',
    }));
  };

  const handleSendFax = async () => {
    if (!selectedFax || !selectedFax.file_path) {
      toast({
        title: 'Error',
        description: 'Please select or upload a file to send',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!formData.fax_number.trim()) {
      toast({
        title: 'Error',
        description: 'Recipient fax number is required',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!formData.sender_email.trim()) {
      toast({
        title: 'Error',
        description: 'Sender email is required',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setSendingFax(true);
    try {
      const hostname = window.location.hostname;
      const baseUrl =
        hostname === 'localhost'
          ? BASE_URLS.local.backend
          : BASE_URLS.render.backend;

      // Get current user info
      let sentByUser = null;
      let sentByUserId = null;
      try {
        const supabaseClient = await getSupabase();
        const { data: { session } } = await supabaseClient.auth.getSession();
        if (session?.user) {
          sentByUserId = session.user.id;
          const { data: userData, error: userError } = await supabasePublic()
            .from('users')
            .select('name, email')
            .eq('id', session.user.id)
            .single();
          
          if (userError) {
            sentByUser = session.user.email;
          } else {
            sentByUser = userData?.name || userData?.email || session.user.email;
          }
        }
      } catch (e) {
        console.warn('Could not get user session:', e);
      }

      const response = await fetch(`${baseUrl}/api/fax/send/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          file_path: selectedFax.file_path,
          fax_number: formData.fax_number.trim(),
          recipient_name: formData.recipient_name.trim(),
          sender_name: formData.sender_name.trim(),
          sender_fax: formData.sender_fax.trim(),
          ...(formData.sender_email.trim() && { sender_email: formData.sender_email.trim() }),
          subject: formData.subject.trim(),
          cover_page: formData.cover_page,
          cover_page_text: formData.cover_page_text.trim(),
          sent_by: sentByUser,
          sent_by_user_id: sentByUserId,
          schema: userSchema,  // Include schema to isolate sent faxes by clinic
        }),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        toast({
          title: 'Fax Queued',
          description: `Fax queued successfully. Queue ID: ${result.queue_id}`,
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
        
        if (onSendSuccess) {
          onSendSuccess(result);
        }
        
        // Reset form
        setFormData({
          fax_number: '1',
          recipient_name: '',
          sender_name: '',
          sender_fax: '',
          sender_email: srfaxAuthEmails.length > 0 ? srfaxAuthEmails[0] : '',
          subject: '',
          cover_page: false,
          cover_page_text: '',
        });
      } else {
        let errorMessage = result.error || 'Failed to send fax';
        
        if (errorMessage.includes('not authorized') || errorMessage.includes('not allowed')) {
          toast({
            title: 'Email Authorization Error',
            description: `${errorMessage}\n\nTo fix this, authorize the email in your SRFax account settings.`,
            status: 'error',
            duration: 15000,
            isClosable: true,
          });
        } else {
          toast({
            title: 'Error',
            description: errorMessage,
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
        }
      }
    } catch (error) {
      console.error('Error sending fax:', error);
      toast({
        title: 'Error',
        description: 'Failed to send fax: ' + error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSendingFax(false);
    }
  };

  return (
    <VStack spacing={4} align="stretch">
      {/* File Upload Section */}
      <Box p={4} border="2px" borderColor="blue.300" borderStyle="dashed" borderRadius="md" bg="blue.50">
        <VStack spacing={3} align="stretch">
          <Text fontSize="md" fontWeight="bold" color="blue.700">
            Upload File to Send
          </Text>
          <FormControl>
            <Input
              type="file"
              accept=".pdf,.tiff,.tif,.png,.jpg,.jpeg"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file && onFileUpload) {
                  onFileUpload(file);
                }
              }}
              isDisabled={uploadingFile}
              display="none"
              id="file-upload-input-tab"
            />
            <Button
              as="label"
              htmlFor="file-upload-input-tab"
              colorScheme="blue"
              variant="outline"
              cursor="pointer"
              isDisabled={uploadingFile}
              width="100%"
            >
              {uploadingFile ? 'Uploading...' : 'Choose File to Upload'}
            </Button>
          </FormControl>
          {uploadingFile && (
            <Box>
              <Progress value={uploadProgress} colorScheme="blue" size="sm" mb={2} />
              <Text fontSize="xs" color="gray.600" textAlign="center">
                {uploadProgress}% uploaded
              </Text>
            </Box>
          )}
          {uploadError && (
            <Alert status="error" size="sm">
              <AlertIcon />
              <AlertDescription fontSize="xs">{uploadError}</AlertDescription>
            </Alert>
          )}
          {uploadedFile && (
            <Box p={2} bg="green.50" borderRadius="md" border="1px" borderColor="green.200" position="relative">
              <HStack justify="space-between" align="flex-start">
                <Box flex={1}>
                  <Text fontSize="sm" fontWeight="bold" color="green.700" mb={1}>
                    ✓ File Uploaded:
                  </Text>
                  <Text fontSize="sm">{uploadedFile.filename}</Text>
                  <Text fontSize="xs" color="gray.600">
                    {(uploadedFile.size / 1024 / 1024).toFixed(2)} MB
                  </Text>
                </Box>
                <IconButton
                  icon={<DeleteIcon />}
                  aria-label="Remove uploaded file"
                  size="sm"
                  colorScheme="red"
                  variant="ghost"
                  onClick={onRemoveUploadedFile}
                />
              </HStack>
            </Box>
          )}
          <Text fontSize="xs" color="gray.500" textAlign="center">
            Supported formats: PDF, TIFF, PNG, JPG (Max 50MB)
          </Text>
        </VStack>
      </Box>

      <Divider />

      {/* File Selection from Inbox */}
      <FormControl>
        <FormLabel>Or Select File from Inbox</FormLabel>
        <Select
          placeholder="Select a file from inbox..."
          value={selectedFax?.file_path || ''}
          onChange={(e) => {
            const fax = allFaxes.find(f => f.file_path === e.target.value);
            if (onSelectFax) onSelectFax(fax || null);
          }}
        >
          {allFaxes.slice(0, 50).map((fax) => (
            <option key={fax.file_path} value={fax.file_path}>
              {fax.filename || fax.file_path?.split('/').pop() || 'Unknown'}
            </option>
          ))}
        </Select>
      </FormControl>

      {selectedFax && (
        <Box p={2} bg="blue.50" borderRadius="md" border="1px" borderColor="blue.200">
          <Text fontSize="sm" fontWeight="bold" mb={1}>Selected File:</Text>
          <Text fontSize="sm">{selectedFax.filename || 'Unknown'}</Text>
        </Box>
      )}

      <Divider />

      {/* Contacts Section */}
      <FormControl>
        <FormLabel>Quick Select Contact</FormLabel>
        {loadingContacts ? (
          <Text fontSize="sm" color="gray.500">Loading contacts...</Text>
        ) : faxContacts.length > 0 ? (
          <VStack spacing={2} align="stretch" maxH="150px" overflowY="auto">
            {faxContacts.map((contact) => (
              <Button
                key={contact.id}
                size="sm"
                variant="outline"
                justifyContent="flex-start"
                onClick={() => selectContact(contact)}
              >
                <Text isTruncated>{contact.name} - {contact.fax_number}</Text>
              </Button>
            ))}
          </VStack>
        ) : (
          <Box p={4} bg="gray.50" borderRadius="md" textAlign="center">
            <Text fontSize="sm" color="gray.500">
              No saved contacts.
            </Text>
          </Box>
        )}
      </FormControl>

      <Divider />

      {/* Recipient Information */}
      <FormControl isRequired>
        <FormLabel>Recipient Fax Number</FormLabel>
        <Input
          value={formData.fax_number}
          onChange={(e) => updateField('fax_number', e.target.value)}
          isDisabled={sendingFax}
          placeholder="1 (US country code - edit for international)"
        />
        <Text fontSize="xs" color="gray.500" mt={1}>
          Accepts formats like: 1(213) 260-2301, 1213-260-2301, 12132602301, etc.
        </Text>
      </FormControl>

      <FormControl>
        <FormLabel>Recipient Name (Optional)</FormLabel>
        <Input
          placeholder="Recipient name"
          value={formData.recipient_name}
          onChange={(e) => updateField('recipient_name', e.target.value)}
          isDisabled={sendingFax}
        />
      </FormControl>

      <Divider />

      {/* Sender Information */}
      <Text fontSize="md" fontWeight="bold">
        Sender Information (Optional)
      </Text>

      <FormControl>
        <FormLabel>Sender Name</FormLabel>
        <Input
          placeholder="Your name or organization"
          value={formData.sender_name}
          onChange={(e) => updateField('sender_name', e.target.value)}
          isDisabled={sendingFax}
        />
      </FormControl>

      <FormControl>
        <FormLabel>Sender Fax Number</FormLabel>
        <Input
          placeholder="Your fax number"
          value={formData.sender_fax}
          onChange={(e) => updateField('sender_fax', e.target.value)}
          isDisabled={sendingFax}
        />
      </FormControl>

      <FormControl isRequired>
        <FormLabel>Sender Email (Required)</FormLabel>
        {srfaxAuthEmails.length > 0 ? (
          <Select
            value={formData.sender_email || (srfaxAuthEmails.length > 0 ? srfaxAuthEmails[0] : '')}
            onChange={(e) => updateField('sender_email', e.target.value)}
            isDisabled={sendingFax}
          >
            {srfaxAuthEmails.map((email) => (
              <option key={email} value={email}>
                {email}
              </option>
            ))}
          </Select>
        ) : (
          <Input
            type="email"
            placeholder="your@email.com (must be authorized in SRFax)"
            value={formData.sender_email}
            onChange={(e) => updateField('sender_email', e.target.value)}
            isDisabled={sendingFax}
          />
        )}
        <Text fontSize="xs" color="gray.500" mt={1}>
          Required by SRFax API. Must be authorized in your SRFax account.
        </Text>
      </FormControl>

      <FormControl>
        <FormLabel>Subject (Optional)</FormLabel>
        <Input
          placeholder="Fax subject line"
          value={formData.subject}
          onChange={(e) => updateField('subject', e.target.value)}
          isDisabled={sendingFax}
        />
      </FormControl>

      <Divider />

      {/* Cover Page Options */}
      <FormControl>
        <Checkbox
          isChecked={formData.cover_page}
          onChange={(e) => updateField('cover_page', e.target.checked)}
          isDisabled={sendingFax}
        >
          Include Cover Page
        </Checkbox>
      </FormControl>

      {formData.cover_page && (
        <FormControl>
          <FormLabel>Cover Page Text (Optional)</FormLabel>
          <Textarea
            rows={4}
            placeholder="Enter cover page message..."
            value={formData.cover_page_text}
            onChange={(e) => updateField('cover_page_text', e.target.value)}
            isDisabled={sendingFax}
          />
        </FormControl>
      )}

      {/* Send Button */}
      <Button
        colorScheme="green"
        onClick={handleSendFax}
        isLoading={sendingFax}
        loadingText="Sending..."
        isDisabled={
          !selectedFax || 
          !formData.fax_number.trim() ||
          !formData.sender_email.trim() ||
          sendingFax
        }
        size="lg"
      >
        Send Fax
      </Button>
    </VStack>
  );
});

SendFaxTabForm.displayName = 'SendFaxTabForm';

export default SendFaxTabForm;
